/**
 * The element that owns the type scale, and the one that says which kind of screen this is.
 *
 * The scale is applied as inline CSS custom properties rather than being written into a
 * stylesheet, so that the number a test reads is the number the browser paints. See
 * `styles/typeScale.ts` for why a `clamp()` was not good enough.
 *
 * Also the safe-area padding: this runs full-screen on an iPad with `viewport-fit=cover`,
 * so the home indicator and the rounded corners are real and the graph has to be inset
 * from them.
 *
 * ## `hc-app--stacked` is stamped here, not by a page
 *
 * It used to be `SessionPage`'s, added to one of its four returns, because the only thing it
 * decided was whether the two strips floating over the graph became rows. It decides the
 * whole phone layout now — every compact padding, gap and radius in the four stylesheets
 * hangs off it — and "the text is all so big" was a report about the app, not about the
 * graph. A class that means "this is a phone" belongs on the element that already measured
 * the viewport, where no screen can be built without one.
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import {
  NARROW_LAYOUT_PX,
  typeScaleFor,
  typeScaleVars,
  type TypeScale,
} from '../../styles/typeScale.js';

/** Sensible default for server-side or test rendering: an iPad in landscape. */
const DEFAULT_WIDTH = 1024;

/**
 * The width at which this is definitely not an iPad, and the chrome may spread out.
 *
 * Above every iPad logical width (1366 is the largest, on the 12.9" in landscape) with a
 * little headroom, so nothing here can change the device the app was designed for. It has
 * a twin in `tokens.css` — `@media (min-width: 1400px)` — and the two must agree, because
 * the sheet decides its entry animation from this while its shape comes from that.
 */
export const WIDE_LAYOUT_PX = 1400;

/**
 * The width at or below which this is a phone, not a tablet.
 *
 * Re-exported rather than declared: it moved into `styles/typeScale.ts` when it started
 * deciding the type scale as well as the layout, because that module has no imports and
 * this one imports it. Its reasoning lives there, and it is still written down exactly once.
 */
export { NARROW_LAYOUT_PX };

export function useViewportWidth(): number {
  const [width, setWidth] = useState(() =>
    typeof window === 'undefined' ? DEFAULT_WIDTH : window.innerWidth,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = (): void => setWidth(window.innerWidth);
    onResize();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  return width;
}

export function useTypeScale(): TypeScale {
  return typeScaleFor(useViewportWidth());
}

/** True on a desktop-sized window, where sheets become side panels. */
export function useWideLayout(): boolean {
  return useViewportWidth() >= WIDE_LAYOUT_PX;
}

/**
 * True on a phone, where a shut branch card shows less and everything is drawn tighter.
 *
 * Deliberately `!useWideLayout()`'s opposite rather than its complement: between the two
 * lies every iPad, which is neither and gets the behaviour it always had.
 */
export function useNarrowLayout(): boolean {
  return useViewportWidth() <= NARROW_LAYOUT_PX;
}

export function AppFrame({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}): React.JSX.Element {
  const width = useViewportWidth();
  const scale = typeScaleFor(width);
  /*
   * One read of the width, two consequences, and they cannot disagree because they are the
   * same comparison: the sizes below go out as inline custom properties, and everything a
   * stylesheet needs to do differently at those sizes hangs off this class.
   */
  const stacked = width <= NARROW_LAYOUT_PX;
  return (
    <div
      className={['hc-app', className, stacked ? 'hc-app--stacked' : '']
        .filter((part) => part !== '')
        .join(' ')}
      data-testid="hc-app-frame"
      style={typeScaleVars(scale) as CSSProperties}
    >
      {children}
    </div>
  );
}
